import { UsersRound } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { formatINR } from '@/shared/utils/currency';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { UserCog, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

interface TeamMember { id: string; username: string; displayName: string; crmRole: string; accountsRole: string; accountsRoleLabel: string; scope: string; approvalLimit: number; permissionCount: number; }
interface RoleTemplate { key: string; label: string; description: string; scope: string; approvalLimit: number; permissionCount: number; }

function useTeam() { return useQuery({ queryKey: ['accounts', 'team'], queryFn: async () => { const { data } = await api.get('/api/accounts/team'); return data as { members: TeamMember[]; totalMembers: number }; } }); }
function useRoleTemplates() { return useQuery({ queryKey: ['accounts', 'team', 'templates'], queryFn: async () => { const { data } = await api.get('/api/accounts/team/role-templates'); return data as RoleTemplate[]; } }); }

export function TeamPage() {
  const { data, isLoading } = useTeam();
  const { data: templates } = useRoleTemplates();
  const qc = useQueryClient();
  const [editUser, setEditUser] = useState<TeamMember | null>(null);
  const [selectedRole, setSelectedRole] = useState('');

  const assignMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => { await api.put(`/api/accounts/team/${userId}/role`, { accountsRole: role }); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['accounts', 'team'] }); setEditUser(null); toast.success('Role assigned'); },
    onError: (err: any) => { toast.error(err.response?.data?.error || 'Failed'); },
  });

  if (isLoading) return <div className=" pb-16"><Skeleton className="h-[400px]" /></div>;

  const scopeLabel = (s: string) => ({ own: 'Own records', team: 'Team', department: 'Department', all: 'All', none: 'None' }[s] || s);

  return (
    <div className="p-6 space-y-4">
      <PageHeader icon={UsersRound} eyebrow="Finance" title="Accounts Team" description={<>Team membership, role assignments and approval limits</>} />

      <Card className="shadow-xs">
        <CardHeader title={`Team Members (${data?.totalMembers || 0})`} />
        <CardBody className="p-0">
          <div className="overflow-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">CRM Role</th><th className="px-4 py-3">Accounts Role</th><th className="px-4 py-3">Scope</th><th className="px-4 py-3 text-right">Approval Limit</th><th className="px-4 py-3 text-center">Permissions</th><th className="px-4 py-3">Actions</th></tr>
              </thead>
              <tbody className="divide-y">
                {(data?.members?.length ?? 0) === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    No team members have an Accounts role yet. Assign one to grant finance access.
                  </td></tr>
                )}
                {data?.members?.map(m => (
                  <tr key={m.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3"><div className="font-medium">{m.displayName}</div><div className="text-xs text-muted-foreground">@{m.username}</div></td>
                    <td className="px-4 py-3 capitalize">{m.crmRole}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">{m.accountsRoleLabel}</span></td>
                    <td className="px-4 py-3 text-xs">{scopeLabel(m.scope)}</td>
                    <td className="px-4 py-3 text-right font-medium">{m.approvalLimit > 0 ? formatINR(m.approvalLimit) : '—'}</td>
                    <td className="px-4 py-3 text-center text-xs text-muted-foreground">{m.permissionCount}</td>
                    <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => { setEditUser(m); setSelectedRole(m.accountsRole || ''); }}><UserCog className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Role Templates Reference */}
      <Card className="shadow-xs">
        <CardHeader title="Available Role Templates" />
        <CardBody className="p-0">
          <div className="overflow-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50"><tr><th className="px-4 py-3">Role</th><th className="px-4 py-3">Description</th><th className="px-4 py-3">Scope</th><th className="px-4 py-3 text-right">Approval Limit</th><th className="px-4 py-3 text-center">Permissions</th></tr></thead>
              <tbody className="divide-y">
                {(templates?.length ?? 0) === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No role templates are configured.
                  </td></tr>
                )}
                {templates?.map(t => (
                  <tr key={t.key} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium flex items-center gap-2"><Shield className="h-4 w-4 text-primary" />{t.label}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.description}</td>
                    <td className="px-4 py-3 capitalize">{t.scope}</td>
                    <td className="px-4 py-3 text-right">{t.approvalLimit > 0 ? formatINR(t.approvalLimit) : '—'}</td>
                    <td className="px-4 py-3 text-center">{t.permissionCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Assign Role Modal */}
      <Modal open={!!editUser} onClose={() => setEditUser(null)} title={`Assign Role — ${editUser?.displayName}`} size="sm" footer={
        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setEditUser(null)}>Cancel</Button><Button onClick={() => editUser && assignMutation.mutateAsync({ userId: editUser.id, role: selectedRole })} disabled={assignMutation.isPending}>{assignMutation.isPending ? 'Saving...' : 'Assign Role'}</Button></div>
      }>
        <div className="py-4 space-y-3">
          <p className="text-sm text-muted-foreground">Current: <strong>{editUser?.accountsRoleLabel}</strong></p>
          <div><label className="text-sm font-medium">New Role Template</label>
            <Select name="role" value={selectedRole} onChange={e => setSelectedRole(e.target.value)} options={[{ value: '', label: '— None (derive from CRM role) —' }, ...(templates || []).map(t => ({ value: t.key, label: `${t.label} (limit: ${formatINR(t.approvalLimit)})` }))]} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
